import { useInfiniteQuery, useQuery } from 'react-query'

import queryClient from '../../queryClient'
import { request } from '../utils/api'
import type { Response } from '../utils/api.types'
import { HTTPMethod } from '../utils/api.types'

import { type EventSpeaker, type EventSpeakerDTO } from './useEventsQuery'

enum WebinarsOrdering {
  RankByConversionsWithinFormat = 'rank_by_conversions_within_format',
  CombinedConversionEngagement = 'combined_conversion_engagement',
}
enum WebinarsLiveFormat {
  Webinar = 'webinar',
}
enum LiveStatus {
  Cancelled = 'cancelled',
  Draft = 'draft',
  Live = 'live',
  Previous = 'previous',
  Upcoming = 'upcoming',
  Ondemand = 'ondemand',
}
enum LiveLabel {
  Cancelled = 'Cancelled',
  Draft = 'Draft',
  Live = 'Live',
  Past = 'Past',
  Upcoming = 'Upcoming',
  Ondemand = 'Ondemand',
}
type RegistrationMode = 'none' | 'all'

interface GetWebinarsRequest {
  include_stats_p?: boolean
  include_private_p?: boolean
  stream_preview_p?: boolean
  include_speakers_p?: boolean
  live_format?: WebinarsLiveFormat
  ordering?: WebinarsOrdering
  order?: 'asc' | 'desc'
  p?: number
  size?: number
  search?: string
  live_status?: LiveStatus
  user_id?: string
  not_user_id?: string
  cancelled_p?: boolean
  draft_p?: boolean
  private_p?: boolean
  template_live_id?: number
  can_download_webinar_recording_p?: boolean
  include_total_count_p?: boolean
  live_series_id?: number
  token?: string
}

interface GetWebinarsResponse extends Response {
  data: WebinarDTO[]
}

interface WebinarDTO {
  live_status: LiveStatus
  live_label: LiveLabel
  live_id: number
  speakers: EventSpeakerDTO[]
  broadcasting_p: boolean
  name: string
  thumbnail_small_download: string
  live_date__date: string
  live_date__time: string
  combined_conversion_conversions_fmt: string
  combined_conversion_views_fmt: string
  combined_conversion_engagement_fmt: string
  rank_by_conversions_within_format: string
  registration_mode: RegistrationMode
  combined_conversion_visits_fmt: string
  combined_conversion_conversion_rate_fmt: string
  combined_conversion_view_rate_fmt: string
  combined_conversion_engagement_per_view_fmt: string
  live_date_epoch: number
  primary_timezone: string
  recording_duration_fmt: string
}

const getWebinars = (data: GetWebinarsRequest) =>
  request<GetWebinarsRequest, GetWebinarsResponse>({
    url: '/api/2/live/list',
    data,
    method: HTTPMethod.POST,
  })

export interface Webinar {
  // Used for type checking.
  type: 'webinar'
  liveStatus: LiveStatus
  liveLabel: LiveLabel
  speakers: EventSpeaker[]
  id: number
  name: string
  thumbnailSmallDownload: string
  liveDate: string
  liveTime: string
  isBroadcasting: boolean
  registrants: string
  attendees: string
  totalEngagement: string
  rankByConversionsWithinFormat: string
  isRegistrationEnabled: boolean
  combinedConversionConversionRateFormatted: string
  recordingDurationFormatted: string
  avgEngagement: string
  combinedConversionViewRateFormatted: string
  liveDateEpoch: number
  timezone: string
  liveSeriesId?: number
}

function getWebinarsQueryFunction(options: Partial<UseWebinarsQueryOptions>) {
  return async ({ pageParam = 1 }) => {
    const {
      includeStats,
      includePrivate,
      includeStreamPreview,
      includeSpeakers,
      liveFormat,
      ordering,
      order,
      page,
      pageSize,
      search,
      liveStatus,
      userId,
      notUserId,
      isCancelled,
      isDraft,
      isPrivate,
      templateId,
      areRawFilesAvailable,
      liveSeriesId,
      token,
    } = options

    const response = await getWebinars({
      include_stats_p: includeStats,
      include_private_p: includePrivate,
      stream_preview_p: includeStreamPreview,
      include_speakers_p: includeSpeakers,
      live_format: liveFormat,
      ordering,
      order,
      p: page ?? pageParam,
      size: pageSize,
      search,
      live_status: liveStatus,
      user_id: userId,
      not_user_id: notUserId,
      cancelled_p: isCancelled,
      draft_p: isDraft,
      private_p: isPrivate,
      template_live_id: templateId,
      can_download_webinar_recording_p: areRawFilesAvailable,
      live_series_id: liveSeriesId,
      token,
    })

    const webinars =
      response.data?.map<Webinar>((webinarDTO) => {
        const speakersDTOs = webinarDTO.speakers ?? []

        const speakers = speakersDTOs.map<EventSpeaker>((speakerDTO) => ({
          avatarUrl: speakerDTO.avatar_url,
          company: speakerDTO.company,
          creationTime: speakerDTO.creation_time,
          email: speakerDTO.email,
          hasAvatar: speakerDTO.has_avatar_p,
          isHidden: speakerDTO.hidden_p,
          liveSpeakerId: speakerDTO.live_speaker_id,
          name: String(speakerDTO.name),
          sortKey: speakerDTO.sortkey,
          title: speakerDTO.title,
        }))

        return {
          type: 'webinar',
          liveStatus: webinarDTO.live_status,
          liveLabel: webinarDTO.live_label,
          id: webinarDTO.live_id,
          speakers,
          name: String(webinarDTO.name),
          thumbnailSmallDownload: webinarDTO.thumbnail_small_download,
          liveDate: webinarDTO.live_date__date,
          liveTime: webinarDTO.live_date__time,
          isBroadcasting: webinarDTO.broadcasting_p,
          registrants: webinarDTO.combined_conversion_conversions_fmt,
          totalEngagement: webinarDTO.combined_conversion_engagement_fmt,
          combinedConversionViewsFormatted:
            webinarDTO.combined_conversion_views_fmt,
          rankByConversionsWithinFormat:
            webinarDTO.rank_by_conversions_within_format,
          isRegistrationEnabled: webinarDTO.registration_mode === 'all',
          combinedConversionConversionRateFormatted:
            webinarDTO.combined_conversion_conversion_rate_fmt,
          combinedConversionViewRateFormatted:
            webinarDTO.combined_conversion_view_rate_fmt,
          attendees: webinarDTO.combined_conversion_views_fmt,
          liveDateEpoch: webinarDTO.live_date_epoch,
          timezone: webinarDTO.primary_timezone,
          recordingDurationFormatted: webinarDTO.recording_duration_fmt,
          avgEngagement: webinarDTO.combined_conversion_engagement_per_view_fmt,
        }
      }) ?? []

    return {
      data: webinars,
      total: response.total_count,
      p: response.p,
    }
  }
}

interface UseWebinarsQueryOptions {
  includeStats: boolean
  includePrivate: boolean
  includeStreamPreview: boolean
  includeSpeakers: boolean
  liveFormat: WebinarsLiveFormat
  ordering: WebinarsOrdering
  order: 'asc' | 'desc'
  page: number
  pageSize: number
  search: string
  liveStatus: LiveStatus
  userId: string
  notUserId: string
  isCancelled: boolean
  isDraft: boolean
  isPrivate: boolean
  templateId: number
  areRawFilesAvailable: boolean
  liveSeriesId: number
  token: string
}

function useWebinarsQuery(options: Partial<UseWebinarsQueryOptions> = {}) {
  return useQuery(['webinars', options], getWebinarsQueryFunction(options))
}

function useWebinarsInfiniteQuery(
  options: Partial<UseWebinarsQueryOptions> = {}
) {
  return useInfiniteQuery(
    ['webinars-infinite', options],
    getWebinarsQueryFunction(options),
    {
      getNextPageParam: (lastPage, pages) =>
        Number(lastPage.total) >
        pages.reduce((acc, cur) => acc + cur.data.length, 0)
          ? lastPage.p + 1
          : undefined,
    }
  )
}

export function prefetchWebinarsInfiniteQuery(
  options: Partial<UseWebinarsQueryOptions> = {}
) {
  return queryClient.prefetchInfiniteQuery(
    ['webinars-infinite', options],
    getWebinarsQueryFunction(options)
  )
}

export {
  getWebinars,
  useWebinarsInfiniteQuery,
  WebinarsOrdering,
  WebinarsLiveFormat,
  LiveStatus,
  LiveLabel,
}

export default useWebinarsQuery
